/**
 * CrmWorkspaceFilter - Filtro de workspace para dashboard CRM
 * 
 * Permite selecionar um workspace ou "Todos".
 * Suporta tamanho compacto via className.
 * 
 * @author Sistema
 * @version 1.1.0 - Suporte a tamanho compacto (40% menor)
 * RELEASE: 2026-10-05 v1.2.0 - Gatilho do filtro segue o modelo de pill: sem a linha cinza da borda, fundo de superfície, sombra sutil e cantos arredondados (rounded-full).
 */

import React, { useState } from 'react';
import { Check, ChevronDown, Layers } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Skeleton } from '@/components/ui/skeleton';

/**
 * Props do componente CrmWorkspaceFilter
 * @param value - ID do workspace selecionado (null = todos)
 * @param onChange - Callback para mudança de seleção
 * @param className - Classes CSS adicionais (suporta h-[22px] para versão compacta)
 */
interface CrmWorkspaceFilterProps {
  value: string | null;
  onChange: (workspaceId: string | null) => void;
  className?: string;
}

// Tipo para workspace
interface Workspace {
  id: string;
  name: string;
  icon: string | null;
  color: string | null;
}

/**
 * Componente de filtro de workspace
 */
export function CrmWorkspaceFilter({
  value,
  onChange,
  className,
}: CrmWorkspaceFilterProps) {
  const { currentTenant } = useAuth();
  const [open, setOpen] = useState(false);

  // Buscar workspaces ativos do tenant
  const { data: workspaces = [], isLoading } = useQuery({
    queryKey: ['crm-workspaces-filter', currentTenant?.id],
    queryFn: async () => {
      if (!currentTenant?.id) return [];
      
      const { data, error } = await supabase
        .from('crm_workspaces')
        .select('id, name, icon, color')
        .eq('tenant_id', currentTenant.id)
        .eq('is_active', true)
        .order('sort_order', { ascending: true })
        .order('name', { ascending: true });

      if (error) throw error;
      return data as Workspace[];
    },
    enabled: !!currentTenant?.id,
    staleTime: 5 * 60 * 1000, // Cache de 5 minutos
  });

  // Workspace selecionado
  const selectedWorkspace = workspaces.find(ws => ws.id === value);

  // Handler para seleção
  const handleSelect = (workspaceId: string | null) => {
    onChange(workspaceId);
    setOpen(false);
  };

  // Detecta se é versão compacta baseado na classe
  const isCompact = className?.includes('h-[22px]');

  if (isLoading) {
    return <Skeleton className={cn(isCompact ? 'h-[22px] w-[100px]' : 'h-9 w-[160px]')} />;
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn(
            'h-9 px-3 gap-2 font-normal border-0 bg-background rounded-full',
            className
          )}
        >
          {selectedWorkspace ? (
            <>
              <div
                className={cn(isCompact ? 'h-3 w-3' : 'h-4 w-4', 'rounded flex items-center justify-center')}
                style={{ backgroundColor: selectedWorkspace.color ? `${selectedWorkspace.color}30` : undefined }}
              >
                <Layers className={cn(isCompact ? 'h-2 w-2' : 'h-3 w-3')} style={{ color: selectedWorkspace.color || undefined }} />
              </div>
              <span className={cn('truncate hidden sm:inline', isCompact ? 'max-w-[60px]' : 'max-w-[100px]')}>{selectedWorkspace.name}</span>
            </>
          ) : (
            <>
              <Layers className={cn(isCompact ? 'h-3 w-3' : 'h-4 w-4', 'opacity-70')} />
              <span className="hidden sm:inline truncate">{isCompact ? 'Todos' : 'Todos Workspaces'}</span>
            </>
          )}
          <ChevronDown className={cn(isCompact ? 'h-2.5 w-2.5' : 'h-3 w-3', 'opacity-50')} />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[220px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Buscar workspace..." />
          <CommandList>
            <CommandEmpty>Nenhum workspace encontrado.</CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="__all__"
                onSelect={() => handleSelect(null)}
                className="cursor-pointer"
              >
                <div
                  className={cn(
                    "mr-2 h-4 w-4 rounded-full border flex items-center justify-center",
                    value === null
                      ? "border-primary bg-primary"
                      : "border-muted"
                  )}
                >
                  {value === null && (
                    <Check className="h-2.5 w-2.5 text-primary-foreground" />
                  )}
                </div>
                <Layers className="h-4 w-4 mr-2 opacity-50" />
                <span>Todos Workspaces</span>
              </CommandItem>
            </CommandGroup>
            <CommandSeparator />
            <CommandGroup heading="Workspaces">
              {workspaces.map(ws => (
                <CommandItem
                  key={ws.id}
                  value={ws.name}
                  onSelect={() => handleSelect(ws.id)}
                  className="cursor-pointer"
                >
                  <div
                    className={cn(
                      "mr-2 h-4 w-4 rounded-full border flex items-center justify-center",
                      value === ws.id
                        ? "border-primary bg-primary"
                        : "border-muted"
                    )}
                  >
                    {value === ws.id && (
                      <Check className="h-2.5 w-2.5 text-primary-foreground" />
                    )}
                  </div>
                  <div
                    className="h-5 w-5 rounded flex items-center justify-center mr-2"
                    style={{ backgroundColor: ws.color ? `${ws.color}20` : undefined }}
                  >
                    <Layers className="h-3 w-3" style={{ color: ws.color || undefined }} />
                  </div>
                  <span className="truncate">{ws.name}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
